import React, { useEffect } from 'react';
import { ProjectApprovalStatus, useProjectApprovals } from './useProjectApprovals';
import { useLanguage } from 'features/hooks/useLanguage';
import { LanguageType } from 'types/index';
import { formatDate } from 'utils/date';
import { useUsersByIds } from 'hooks/store';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from 'features/components/Table';
import { Button } from 'features/components/Button';
import { UserDetails } from 'api/data/accounts';
import { useTranslatedMetadataLabel } from 'hooks/useTranslatedMetadataLabel';
import { toast } from 'react-toastify';
import { useIsCodelistFeatureEnabled } from '../codelist/useIsCodeListEnabled';

interface ProjectApprovalsTableProps {}

const DICTIONARY = {
	[LanguageType.English]: {
		loading: 'Loading approvals...',
		failedToLoad: 'Failed to load project approvals',
		projectApprovals: 'Project Approvals',
		manageApprovals: 'Manage all pending project approvals',
		organization: 'Organization',
		projectName: 'Project Name',
		projectUser: 'Project User',
		date: 'Date',
		decisionDate: 'Decision Date',
		decisionMaker: 'Decision Maker',
		actions: 'Actions',
		approve: 'Approve',
		approved: 'Approved',
		decline: 'Decline',
		declined: 'Declined',
		deleted: 'Deleted',
		showing: 'Showing',
		approval: 'approval',
		approvals: 'approvals',
		somethingWentWrong:
			'Something went wrong, please try again later or contact your administrator',
		archiveNumber: 'Public 360 archive number',
		projectType: 'Project Type',
		pii: 'PII (personal identifiable information)'
	},
	[LanguageType.Norwegian]: {
		loading: 'Laster godkjennelser...',
		failedToLoad: 'Kunne ikke laste prosjektgodkjennelser',
		projectApprovals: 'Prosjektgodkjennelser',
		manageApprovals: 'Administrer alle ventende prosjektgodkjennelser',
		organization: 'Organisasjon',
		projectName: 'Prosjektnavn',
		projectUser: 'Prosjektbruker',
		date: 'Dato',
		decisionDate: 'Beslutningsdato',
		decisionMaker: 'Beslutningstaker',
		actions: 'Handlinger',
		approve: 'Godkjenn',
		decline: 'Avslå',
		approved: 'Godkjent',
		declined: 'Avvist',
		deleted: 'Slettet',
		showing: 'Viser',
		approval: 'godkjennelse',
		approvals: 'godkjennelser',
		somethingWentWrong:
			'Noe gikk galt, vennligst prøv igjen senere eller kontakt din administrator',
		archiveNumber: 'Public 360 arkivnummer',
		projectType: 'Prosjekttype',
		pii: 'PII (personlig identifiserbar informasjon)'
	}
};

export const ProjectApprovalsTable: React.FC<ProjectApprovalsTableProps> = () => {
	const {
		approvals,
		isLoading,
		isQueryError,
		approveProject,
		declineProject,
		approveProjectMutation,
		declineProjectMutation
	} = useProjectApprovals();

	const [usersById, getUsersByIds] = useUsersByIds();

	useEffect(() => {
		if (approvals && approvals.length > 0) {
			const allUserIds = approvals
				.flatMap(a => [a.requesterUserId, a.decisionUserId])
				.filter((id): id is string => id !== null && id !== undefined);
			getUsersByIds(Array.from(new Set(allUserIds)));
		}
	}, [approvals, getUsersByIds]);

	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const getTranslatedMetadataLabel = useTranslatedMetadataLabel();
	const handleApprove = async (projectId: number) => {
		try {
			await approveProject({ projectId });
		} catch (error) {
			console.error('Failed to approve project:', error);
			toast.error(dictionary.somethingWentWrong);
		}
	};

	const handleDecline = async (projectId: number) => {
		try {
			await declineProject({ projectId });
		} catch (error) {
			console.error('Failed to decline project:', error);
			toast.error(dictionary.somethingWentWrong);
		}
	};
	const isCodelistFeatureEnabled = useIsCodelistFeatureEnabled();

	const renderUserNameCell = (userId: string, userData?: UserDetails) => {
		const hasUserIds = approvals?.length > 0;
		const isLoadingUserData = hasUserIds && !userData && Object.keys(usersById).length === 0;

		if (isLoadingUserData) {
			return (
				<div className="flex flex-col animate-pulse">
					<div className="h-5 w-32 bg-gray-200 rounded"></div>
					<span className="text-sm text-gray-500 mt-1">{userId}</span>
				</div>
			);
		}

		if (userData) {
			return (
				<div className="flex flex-col">
					<span>
						{userData.userFirstName} {userData.userSirName}
					</span>
					<span className="text-sm text-gray-500">{userId}</span>
				</div>
			);
		}

		return userId;
	};

	if (isLoading) {
		return (
			<div className="w-full flex items-center justify-center p-10">
				<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500"></div>
				<p className="mt-4 text-gray-600 font-medium">{dictionary.loading}</p>
			</div>
		);
	}

	if (isQueryError) {
		return (
			<div className="w-full rounded-xl shadow-md p-5 bg-red-50 border border-red-200">
				<p className="text-red-600 font-medium text-center">{dictionary.failedToLoad}</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<div>
				<span className="table-info">
					{dictionary.showing} {approvals?.length || 0}{' '}
					{approvals?.length !== 1 ? dictionary.approvals : dictionary.approval}
				</span>
			</div>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>
							<div className="table-header-label">{dictionary.projectName}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.projectUser}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.organization}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.archiveNumber}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.projectType}</div>
						</TableHead>
						{isCodelistFeatureEnabled && (
							<TableHead>
								<div className="table-header-label">{dictionary.pii}</div>
							</TableHead>
						)}
						<TableHead>
							<div className="table-header-label">{dictionary.date}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.decisionDate}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.decisionMaker}</div>
						</TableHead>
						<TableHead>
							<div className="table-header-label">{dictionary.actions}</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{approvals?.map(approval => {
						const requesterUserData = usersById[approval.requesterUserId];
						const decisionUserData = approval.decisionUserId
							? usersById[approval.decisionUserId]
							: undefined;
						return (
							<TableRow key={approval.id}>
								<TableCell>{approval.projectName}</TableCell>
								<TableCell>
									{renderUserNameCell(
										approval.requesterUserId,
										requesterUserData
									)}
								</TableCell>
								<TableCell>{approval.organizationName}</TableCell>
								<TableCell>{approval.archiveNumber || '-'}</TableCell>
								<TableCell>
									{getTranslatedMetadataLabel(approval.projectType || '') || '-'}
								</TableCell>
								{isCodelistFeatureEnabled && (
									<TableCell>{approval.codeListEnabled ? 'Yes' : 'No'}</TableCell>
								)}
								<TableCell>{formatDate(approval.createdAt)}</TableCell>
								<TableCell>
									{approval.decisionDate
										? formatDate(approval.decisionDate)
										: '-'}
								</TableCell>
								<TableCell>
									{approval.decisionUserId
										? renderUserNameCell(
												approval.decisionUserId,
												decisionUserData
										  )
										: '-'}
								</TableCell>
								<TableCell>
									<div className="flex gap-2">
										{approval.status === ProjectApprovalStatus.PENDING ? (
											<>
												<Button
													variant="primary"
													text={dictionary.approve}
													onClick={() =>
														handleApprove(approval.projectId)
													}
													loading={
														approveProjectMutation.isLoading &&
														approveProjectMutation.variables
															?.projectId === approval.projectId
													}
												/>
												<Button
													variant="destructive"
													text={dictionary.decline}
													onClick={() =>
														handleDecline(approval.projectId)
													}
													loading={
														declineProjectMutation.isLoading &&
														declineProjectMutation.variables
															?.projectId === approval.projectId
													}
												/>
											</>
										) : (
											<span>
												{approval.status === ProjectApprovalStatus.DELETED
													? dictionary.deleted
													: approval.status ===
													  ProjectApprovalStatus.REJECTED
													? dictionary.declined
													: dictionary.approved}
											</span>
										)}
									</div>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</div>
	);
};
